import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import ServiceCard from '../../components/ServiceCard/ServiceCard'
import {_L, useLocaleState} from '../../context/LocaleContext'
import {isServiceCatalog} from '../../utils/routing'
import {useViewFetch} from '../../utils/fetch'
import {fetchServiceCatalogTypes} from '../../api/servicerequest'
import {isEmpty} from '../../utils/helpers'
import {SERVICE_SEQUENCE} from '../../constants/const'
import {useUIDispatch} from '../../context/UIContext'
import {getAvailableActions, useConfigState} from '../../context/ConfigContext'
import ServiceCatalogSearch from './ServiceCatalogSearch'

const getChildren = ({serviceCatalogTypes, keys}) => {
  let level = keys.length
  switch (level) {
    case 0:
      return {types: serviceCatalogTypes}
    case 1:
      return {types: serviceCatalogTypes[keys[0]].children}
    case 2:
      return {types: serviceCatalogTypes[keys[0]].children[keys[1]].children}
    default:
      return {types: null}
  }
}

const getSearchServiceTypes = ({serviceCatalogTypes, keys}) => {
  let items = []
  const {types} = getChildren({serviceCatalogTypes, keys})
  Object.values(types).forEach(serviceCatalogType => {
    items.push(serviceCatalogType)
    const {children} = serviceCatalogType
    if (!isEmpty(children)) {
      Object.values(children).forEach(serviceCatalogType => {
        items.push(serviceCatalogType)
        const {children} = serviceCatalogType
        if (!isEmpty(children)) {
          Object.values(children).forEach(serviceCatalogType => {
            items.push(serviceCatalogType)
          })
        }
      })
    }
  })
  return Object.values(items).sort((a, b) => a.level - b.level)
}

const BreadCrumbs = ({keys = [], descriptions, onClick}) => {
  const localeState = useLocaleState()

  const onBreadCrumbItemClick = ({key, applyHighlight}) => {
    if (applyHighlight) {
      onClick(key)
    }
  }

  // Add All Services
  const AllServices = [
    <BreadCrumbItem
      key="all-services"
      onClick={() => onClick('ALL_SERVICES')}
      highlight
    >
      {_L({key: 'All Services', localeState})}
    </BreadCrumbItem>
  ]

  let breadCrumbItems = keys.map((key, idx) => {
    let applyHighlight = idx < keys.length - 1
    return (
      <div key={key} style={{display: 'flex'}}>
        <ArrowContainer>></ArrowContainer>
        <BreadCrumbItem
          onClick={() => onBreadCrumbItemClick({key, applyHighlight})}
          highlight={applyHighlight}
        >
          {descriptions[key]}
        </BreadCrumbItem>
      </div>
    )
  })

  let items = [...AllServices, ...breadCrumbItems]

  if (breadCrumbItems.length === 0) {
    items = []
  }

  return <BreadCrumbContainer>{items}</BreadCrumbContainer>
}

const ServiceCatalogItems = ({parentPage, onCatalogItemChange}) => {
  const localeState = useLocaleState()
  const uiDispatch = useUIDispatch()
  const configState = useConfigState()
  const availableActions = getAvailableActions(configState)
  const {ServiceCatalogSearch: isServiceCatalogSearch} = configState.params
  const [catalogItems, setCatalogItems] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [keys, setKeys] = useState([])

  const isCatalogView = isServiceCatalog()

  const {data, error} = useViewFetch({
    api: fetchServiceCatalogTypes,
    parameters: {
      includeActions: isCatalogView,
      appParameters: availableActions,
      localeState
    },
    onChangeOf: [],
    initialState: {data: {}, descriptions: {}}
  })

  // TODO: Need better presentation
  if (error) {
    alert(error)
  }

  const {data: serviceCatalogTypes, descriptions} = data

  useEffect(
    () => {
      const {types} = getChildren({serviceCatalogTypes, keys})
      if (types) {
        const items = Object.values(types).sort(
          (a, b) => a.displayOrder - b.displayOrder
        )
        setCatalogItems(items)
      }

      let title = ''
      if (keys.length > 0) {
        const key = keys[keys.length - 1]
        title = descriptions[key]
      }
      onCatalogItemChange({title})
    },
    [serviceCatalogTypes, keys, onCatalogItemChange, descriptions]
  )

  const onClickCatalogItem = ({
    ids,
    children,
    activityType,
    probType,
    hideFields,
    instructions,
    isStatic = false
  }) => {
    const parent = isEmpty(parentPage) ? null : parentPage
    if (isEmpty(children)) {
      if (isStatic) {
        const [content] = ids
        uiDispatch({
          type: 'setContent',
          payload: {content, parentPage: parent || 'SERVICE_CATALOG'}
        })
      } else {
        const catalogHeader = descriptions[ids[ids.length - 1]]
        uiDispatch({
          type: 'setContent',
          payload: {
            content: SERVICE_SEQUENCE,
            parentPage: parent || 'SERVICE_CATALOG',
            props: {
              activityType,
              probType,
              initialForm: 'LOCATION',
              catalogHeader,
              hideFields,
              instructions
            }
          }
        })
      }
    } else {
      setKeys(ids)
    }
    setSearchValue('')
  }

  const onBreadCrumbClicked = crumb => {
    if (crumb === 'ALL_SERVICES') {
      setKeys([])
    } else {
      let crumbIndex = keys.indexOf(crumb)
      let numberOfKeys = keys.length

      if (crumbIndex < numberOfKeys) {
        const newKeys = keys.slice(0)
        newKeys.pop()
        setKeys(newKeys)
      }
    }
  }

  const onSearch = value => {
    let upperCaseValue = value.toUpperCase()
    if (value) {
      const items = getSearchServiceTypes({serviceCatalogTypes, keys}).filter(
        item => {
          const {activityType, title, description = '', keywords = ''} = item
          // split by semicolon then join with nothing to apply includes in string
          const keywordsList = keywords
            .split(';')
            .join('')
            .trim()
          return (
            activityType.toUpperCase().includes(upperCaseValue) ||
            title.toUpperCase().includes(upperCaseValue) ||
            description.toUpperCase().includes(upperCaseValue) ||
            keywordsList.toUpperCase().includes(upperCaseValue)
          )
        }
      )
      setCatalogItems(items)
    } else {
      setKeys([...keys])
    }
    setSearchValue(value)
  }

  return (
    <Container>
      <Wrapper>
        {isServiceCatalogSearch === '1' && (
          <ServiceCatalogSearch searchValue={searchValue} onSearch={onSearch} />
        )}
        <BreadCrumbs
          keys={keys}
          descriptions={descriptions}
          onClick={onBreadCrumbClicked}
        />
        <ItemContainer>
          {catalogItems.map(item => {
            const {activityType} = item
            return (
              <ServiceCard
                key={activityType}
                cardData={item}
                onClick={onClickCatalogItem}
                dataTestId={activityType}
              />
            )
          })}
          <Spacer />
        </ItemContainer>
      </Wrapper>
    </Container>
  )
}

export default ServiceCatalogItems

ServiceCatalogItems.defaultProps = {
  onCatalogItemChange: () => {}
}

const Wrapper = styled.div`
  margin: 0 auto;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
`
const Container = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  padding: 50px 40px 40px 40px;
  box-sizing: border-box;
  background-color: #eff0f4;
  overflow: auto;

  @media (max-width: 1024px) {
    padding: 24px 40px 40px 40px;
  }

  @media (max-width: 780px) {
    padding: 20px 40px 40px 40px;
  }

  @media (max-width: 420px) {
    padding: 6px 16px 6px 16px;
  }
`

const ItemContainer = styled.div`
  display: grid;
  grid-template-columns: 685px 685px;
  grid-auto-rows: 120px;
  grid-row-gap: 32px;
  grid-column-gap: 57px;
  justify-items: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  max-width: 1427px;
  margin: 0 auto;

  @media (max-width: 1500px) {
    grid-template-columns: 560px 560px;
    grid-column-gap: 34px;
    grid-row-gap: 30px;
    max-width: 1154px;
  }

  @media (max-width: 1245px) {
    grid-template-columns: 450px 450px;
    grid-auto-rows: 100px;
    max-width: 934px;
  }

  @media (max-width: 1023px) {
    grid-template-columns: 100%;
  }

  @media (max-width: 780px) {
    grid-row-gap: 24px;
  }

  @media (max-width: 420px) {
    grid-auto-rows: 80px;
    grid-row-gap: 13px;
  }
`

const BreadCrumbContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: baseline;
  box-sizing: border-box;
  font-weight: bold;
  font-size: 20px;
  color: #a4a9b7;
  padding: 12px 0 12px 0;
  height: auto;
  width: 1427px;
  margin: 0 auto;

  @media (max-width: 1500px) {
    width: 1154px;
  }

  @media (max-width: 1245px) {
    width: 934px;
  }

  @media (max-width: 1023px) {
    width: 700px;
  }

  @media (max-width: 780px) {
    font-size: 17px;
    width: 100%;
  }

  @media (max-width: 420px) {
    font-size: 14px;
  }
`

const BreadCrumbItem = styled.div`
  box-sizing: border-box;
  user-select: none;
  white-space: nowrap;
  color: ${props => (props.highlight ? '#3395fe' : '#a4a9b7')};
  cursor: ${props => (props.highlight ? 'pointer' : 'default')};
  padding-right: 10px;
  padding-bottom: 24px;

  @media (max-width: 780px) {
    padding-bottom: 10px;
  }

  @media (max-width: 420px) {
    padding-right: 6px;
    padding-bottom: 4px;
  }
`

const ArrowContainer = styled.div`
  box-sizing: border-box;
  user-select: none;
  color: #a4a9b7;
  padding-right: 10px;
  padding-bottom: 24px;

  @media (max-width: 780px) {
    padding-bottom: 10px;
  }

  @media (max-width: 420px) {
    padding-right: 6px;
    padding-bottom: 4px;
  }
`

const Spacer = styled.div`
  height: 50px;
  width: 100%;
`
